import type { Customer } from '../../types/customers.types';
import type { EditCustomerFormInputs, EditPendingCustomerFormInputs } from '../../schemas/customer.schemas';

// Mode A submits EditCustomerFormInputs (contact only); Mode B submits
// EditPendingCustomerFormInputs (contact + title/forename/surname); the holder
// name change submits the three name fields alone.
type NameFields = Pick<EditPendingCustomerFormInputs, 'title' | 'forename' | 'surname'>;
type ProposedFields = EditCustomerFormInputs | EditPendingCustomerFormInputs | NameFields;

export interface DiffEntry {
    field: keyof EditPendingCustomerFormInputs;
    label: string;
    originalValue: string;
    newValue: string;
}

const fieldMap: Record<
    keyof EditPendingCustomerFormInputs,
    { label: string; customerKey: keyof Customer }
> = {
    addressLine1: { label: 'Address Line 1', customerKey: 'address_line_1' },
    addressLine2: { label: 'Address Line 2', customerKey: 'address_line_2' },
    city: { label: 'City', customerKey: 'city' },
    postcode: { label: 'Postcode', customerKey: 'postcode' },
    email: { label: 'Email', customerKey: 'email' },
    mobilePhone: { label: 'Mobile Phone', customerKey: 'mobile_phone' },
    businessName: { label: 'Business Name', customerKey: 'business_name' },
    vatNumber: { label: 'VAT Number', customerKey: 'vat_number' },
    title: { label: 'Title', customerKey: 'title' },
    forename: { label: 'Forename', customerKey: 'forename' },
    surname: { label: 'Surname', customerKey: 'surname' },
};

// Normalise empty-ish values so '' === undefined === null for diff purposes.
const norm = (v: string | undefined | null): string => (v ?? '').trim();

export function computeCustomerDiff(original: Customer, proposed: ProposedFields): DiffEntry[] {
    const entries: DiffEntry[] = [];
    for (const [camelKey, { label, customerKey }] of Object.entries(fieldMap) as Array<
        [keyof EditPendingCustomerFormInputs, { label: string; customerKey: keyof Customer }]
    >) {
        // A key the caller did not submit is skipped, so a contact-only edit shows
        // no "name removed" entry and a name-only change no "address removed" one.
        if (!(camelKey in proposed)) continue;
        const originalValue = norm(original[customerKey] as string | undefined);
        const newValue = norm((proposed as Record<string, string | undefined>)[camelKey]);
        if (originalValue !== newValue) {
            entries.push({ field: camelKey, label, originalValue, newValue });
        }
    }
    return entries;
}

// Merges diff entries into the original Customer, preserving id and any
// unedited/read-only fields. Replaces diffToPayload (which fed the now-removed
// PUT /customers/{id} call) — the merged Customer is handed to onSavePending
// instead of a partial API payload.
export function applyCustomerDiff(original: Customer, diff: DiffEntry[]): Customer {
    const merged: Customer = { ...original };
    for (const entry of diff) {
        const { customerKey } = fieldMap[entry.field];
        (merged as unknown as Record<string, string>)[customerKey] = entry.newValue;
    }
    return merged;
}
